import {
  useIssuePermissions,
  useRelationshipTypes,
} from '@issuemanager/client/features/issues';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { GetUserRolesResponseDto } from '@issuemanager/api-client';
import {
  APP_ERROR_TYPE,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
} from '@issuemanager/apps/client/contexts';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { paths } from '@issuemanager/apps/client/paths';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import {
  IssueStatusActionable,
  IssueStatusEnum,
  IssueStatusLateActionable,
} from '@issuemanager/constants';
import { UseQueryResult } from '@tanstack/react-query';

export default function useIssueAuthorization(
  code: string | undefined,
  issueStatus?: string,
  readableFields?: readonly string[],
): {
  userRoles: UseQueryResult<GetUserRolesResponseDto, ExtendedAxiosError>;
  canManage: boolean | undefined;
  canViewRelationships: boolean | undefined;
  canManageRelationships: boolean | undefined;
} {
  const appErrorHandler = useAppErrorHandler();
  const isIssueRelationshipsEnabled = useFeatureFlags('issueRelationships');
  const { pathname } = useLocation();

  const issuePermissions = useIssuePermissions(code);
  const { isLoading: isLoadingRelationshipTypes } = useRelationshipTypes();

  // Combined loading state to avoid duplication
  const isLoading = issuePermissions.isLoading || isLoadingRelationshipTypes;

  const canView = useMemo(() => {
    if (!readableFields || !issueStatus) {
      return undefined;
    }

    return readableFields.length > 0;
  }, [readableFields, issueStatus]);

  const canManage =
    issuePermissions.data?.isCreator ||
    issuePermissions.data?.isIssueTypeManager ||
    issuePermissions.data?.isOwner ||
    issuePermissions.data?.isApprover ||
    issuePermissions.data?.isWatcher ||
    issuePermissions.data?.isActionOwner ||
    issuePermissions.data?.isActionApprover;

  const canViewRelationships = useMemo(() => {
    if (
      !isIssueRelationshipsEnabled ||
      !readableFields ||
      !issueStatus ||
      isLoadingRelationshipTypes
    ) {
      return undefined;
    }

    return canView;
  }, [
    isIssueRelationshipsEnabled,
    readableFields,
    issueStatus,
    isLoadingRelationshipTypes,
    canView,
  ]);

  const canManageRelationships = useMemo(() => {
    const { isIssueTypeManager, isOwner, isCreator } =
      issuePermissions?.data || {};

    const isActionableStatus =
      IssueStatusActionable.includes(issueStatus as IssueStatusEnum) ?? false;
    const isLateActionableStatus =
      IssueStatusLateActionable.includes(issueStatus as IssueStatusEnum) ??
      false;
    const isDraftStatus = issueStatus === IssueStatusEnum.DRAFT;

    const hasAllowedRoleWithStatus =
      (isIssueTypeManager && isActionableStatus) ||
      (isOwner && isLateActionableStatus) ||
      (isCreator && isDraftStatus);

    if (canViewRelationships && hasAllowedRoleWithStatus) {
      return true;
    }

    return false;
  }, [canViewRelationships, issuePermissions.data, issueStatus]);

  useEffect(() => {
    if (isLoading) return;

    if (!canManage && pathname.includes(paths.issues.manage)) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.UNAUTHORIZED_TO_MANAGE_ISSUE,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    }

    if (
      canViewRelationships === false &&
      pathname.includes(paths.issues.relationships)
    ) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.UNAUTHORIZED_TO_VIEW_RELATIONSHIPS,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    }
  }, [isLoading, canManage, canViewRelationships, pathname, appErrorHandler]);

  return {
    userRoles: issuePermissions,
    canManage,
    canViewRelationships,
    canManageRelationships,
  };
}
